<template>
  <fieldset class="form-section" :disabled="disabled">
    <legend v-if="$slots.heading"><slot name="heading" /></legend>
    
	<div v-if="showLines" class="grid gap-16 sm-2s">
		<AddressField style="min-width: 12em;" v-bind="fieldProps('line1')" v-on="fieldListeners('line1')" line="1" />
		<AddressField style="min-width: 12em;" v-bind="fieldProps('line2')" v-on="fieldListeners('line2')" line="2" />
		<CountryField v-bind="fieldProps('country')" v-on="fieldListeners('country')" />
		<CityField style="min-width: 12em;" v-bind="fieldProps('city')" v-on="fieldListeners('city')" />
		<RegionField v-bind="fieldProps('region')" v-on="fieldListeners('region')" />
		<PostalCodeField v-bind="fieldProps('postalCode')" v-on="fieldListeners('postalCode')" />
    </div>
  </fieldset>
</template>

<script>
import { toString } from '@/helpers/convert';
import { toCountry, toRegion } from '@/helpers/geo';

export default {
  name: 'AddressFormFields',
  props: {
    /** @type {import('vue/types/options').PropOptions<boolean | null | undefined>} */
    billing: {
      type: Boolean,
      default: false,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    city: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    cityError: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    country: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    countryError: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<boolean | null | undefined>} */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    line1: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    line1Error: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    line2: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    line2Error: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<boolean | null | undefined>} */
    noLines: {
      type: Boolean,
      default: false,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    postalCode: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    postalCodeError: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    region: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    regionError: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<boolean | null | undefined>} */
    required: {
      type: Boolean,
      default: false,
    },
    /** @type {import('vue/types/options').PropOptions<boolean | null | undefined>} */
    shipping: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /**
     * @returns {boolean}
     */
    isDisabled() {
      return !!this.disabled;
    },
    /**
     * @returns {boolean}
     */
    isRequired() {
      return !!this.required;
    },
    /**
     * @returns {import('@/helpers/geo').Country | null}
     */
    resolvedCountry() {
      return toCountry(this.country);
    },
    /**
     * @returns {import('@/helpers/geo').Country | null}
     */
    resolvedRegion() {
      return toRegion(this.region);
    },
    /**
     * @returns {boolean}
     */
    showLines() {
      return !this.noLines;
    },
  },
  methods: {
    /**
     * @param {'country' | 'city' | `line${1 | 2}` | 'postalCode' | 'region'} name
     * @returns {Readonly<Record<string, Function | Function[]>>}
     */
    fieldListeners(name) {
      const listeners = Object.create(null);
      /** @type {'change' | 'input'} */
      let valueEvent;
      switch (name) {
        case 'country':
        case 'region':
          valueEvent = 'change';
          break;
        default:
          valueEvent = 'input';
          break;
      }
      listeners[valueEvent] = (value) => {
        if (value instanceof Event) {
          value = toString(value.target?.value);
        }
        this.$emit(`update:${name}`, value);
      };
      listeners['update:error'] = (value) => this.$emit(`update:${name}Error`, value);
      return Object.freeze(listeners);
    },
    /**
     * @param {'country' | 'city' | `line${1 | 2}` | 'postalCode' | 'region'} name
     * @returns {Record<string, unknown>}
     */
    fieldProps(name) {
      /** @type {Record<string, unknown>} */
      const props = Object.create(null);
      if (this.billing) {
        props.billing = true;
      } else if (this.shipping) {
        props.shipping = true;
      }
      if (this.required && name !== 'line2') {
        props.required = true;
      }
      props.country = this.resolvedCountry;
      props.error = this[`${name}Error`];
      props.name = name;
      props.region = this.resolvedRegion;
      props.value = this[name];
      return Object.freeze(props);
    },
  },
};
</script>
